// src/components/mapScenes/Route333Scene.jsx
import Phaser from "phaser";
import AudioManager from "../../../@shared/utils/AudioManager";

import route333BG from "../GameMap/NVCBG.png";
import route333FG from "../GameMap/NVCGFG.png";
import route333TMJ from "../../../@shared/assets/GameMaps/VoidCity/route-333.json";
import npcMap from "../../../@shared/assets/GameMaps/npcMap";
import {
  getAvatarSpriteConfig,
  scaleAvatarBodySize,
} from "../../../@shared/utils/SpritesMap";
import { createWalkAnimations } from "../../../@shared/utils/SceneHelpers";
import {
  buildObstacleIndex,
  computePlayableRect,
} from "../../../@shared/utils/worldSceneHelpers";
import {
  ORB_FRAMES,
  ORB_GLASS,
  redrawOrbGlass,
  spawnWorldOrbs,
} from "../../../@shared/utils/orbHelpers";
import {
  createLocalOrbAvatarImage,
  getLocalOrbAvatarConfig,
  preloadLocalOrbAvatar,
  updateLocalOrbAvatar,
} from "../../../@shared/utils/phaserOrbAvatar";
import { applyWorldSceneBackground } from "../../../@shared/utils/worldSceneBackgrounds";
const DOOR_SFX = require("../../../@shared/assets/sounds/door.mp3");

// If you want the city spawn to drop the player right in front of the tower door
// we reuse the polygon from the city collisions:
const cityCollisions = require("../GameMap/newCollisions.json");

const DEBUG_COLLIDERS = false;

// Simple AABB helpers
const aabbFromBody = (body) => {
  const b = body.bounds;
  return { x: b.min.x, y: b.min.y, w: b.max.x - b.min.x, h: b.max.y - b.min.y };
};

const rectsOverlap = (a, b, pad = 0) =>
  a.x < b.x + b.w + pad &&
  a.x + a.w > b.x - pad &&
  a.y < b.y + b.h + pad &&
  a.y + a.h > b.y - pad;

export default class Route333Scene extends Phaser.Scene {
  constructor() {
    super({ key: "Route333Scene" });
    this.tmj = route333TMJ;
    this.tmjLayer = "TestCollisions";
  }

  init(data = {}) {
    this.spawnPoint = data.spawn || { x: 1024, y: 1024 };
    // this.heroUrl = require("../../../@shared/assets/GameMaps/CharactaerMovementSpritesheet.png");
    this.heroUrl =
      data.heroUrl ||
      require("../../../@shared/assets/GameMaps/CharactaerMovementSpritesheet.png");
    this.heroAvatarKey = data.heroAvatarKey || "io_default";
    this.heroSpriteConfig = getAvatarSpriteConfig(this.heroAvatarKey);
    this.userData = data.userData || null;
    const { avatarData, textureKey } = getLocalOrbAvatarConfig(this.userData);
    this.localOrbAvatarData = avatarData;
    this.localOrbTextureKey = textureKey;
  }

  // ---- helpers -------------------------------------------------------------
  static extractCollisionDataFromTiled(
    tiledJson,
    layerName = "TestCollisions",
  ) {
    const layer =
      tiledJson?.layers?.find(
        (l) =>
          (l.type === "objectgroup" || l.type === "object") &&
          l.name === layerName,
      ) || null;

    if (!layer) return { buildings: [] };

    const abs = (o, pts) => pts.map((pt) => [o.x + pt.x, o.y + pt.y]);

    const buildings = (layer.objects || []).flatMap((o) => {
      if (Array.isArray(o.polygon)) {
        return [{ name: o.name || o.type || "", polygon: abs(o, o.polygon) }];
      }
      if (o.width && o.height) {
        const poly = [
          [o.x, o.y],
          [o.x + o.width, o.y],
          [o.x + o.width, o.y + o.height],
          [o.x, o.y + o.height],
        ];
        return [{ name: o.name || o.type || "", polygon: poly }];
      }
      if (Array.isArray(o.polyline)) {
        const pts = abs(o, o.polyline);
        const xs = pts.map((p) => p[0]);
        const ys = pts.map((p) => p[1]);
        const poly = [
          [Math.min(...xs), Math.min(...ys)],
          [Math.max(...xs), Math.min(...ys)],
          [Math.max(...xs), Math.max(...ys)],
          [Math.min(...xs), Math.max(...ys)],
        ];
        return [{ name: o.name || o.type || "", polygon: poly }];
      }
      return [];
    });

    return { buildings };
  }

  drawCollisionDebugBox(x, y, w, h, color = 0xff0000, alpha = 0.25) {
    const g = this.add.graphics().setDepth(9999);
    g.lineStyle(1, color, 1);
    g.fillStyle(color, alpha);
    g.strokeRect(x - w / 2, y - h / 2, w, h);
    g.fillRect(x - w / 2, y - h / 2, w, h);
  }

  drawPlayerDebugBox() {
    if (!this.player?.body) return;
    const b = this.player.body.bounds;
    const w = b.max.x - b.min.x;
    const h = b.max.y - b.min.y;
    const x = (b.min.x + b.max.x) / 2;
    const y = (b.min.y + b.max.y) / 2;

    this.playerDebugGfx.clear();
    this.playerDebugGfx.lineStyle(2, 0xffff00, 1);
    this.playerDebugGfx.fillStyle(0xffff00, 0.12);
    this.playerDebugGfx.strokeRect(x - w / 2, y - h / 2, w, h);
    this.playerDebugGfx.fillRect(x - w / 2, y - h / 2, w, h);
  }

  // ---- lifecycle -----------------------------------------------------------
  preload() {
    this.load.spritesheet(
      "hero",
      this.heroUrl,
      {
        frameWidth: this.heroSpriteConfig.frameWidth,
        frameHeight: this.heroSpriteConfig.frameHeight,
      },
    );
    preloadLocalOrbAvatar(this, this.localOrbAvatarData, this.localOrbTextureKey);
    this.load.image("route333_bg", route333BG);
    this.load.image("route333_fg", route333FG);

    // ✅ Ensure orb textures exist in this scene too (mirrors VoidCity preload)
    Object.values(npcMap).forEach((npc) => {
      if (this.textures.exists(npc.key)) return;
      if (npc.frameWidth && npc.frameHeight) {
        this.load.spritesheet(npc.key, npc.url, {
          frameWidth: npc.frameWidth,
          frameHeight: npc.frameHeight,
        });
      } else {
        this.load.image(npc.key, npc.url);
      }
    });

    AudioManager.loadSFX("door", DOOR_SFX);
  }

  create(data = {}) {
    applyWorldSceneBackground(this);
    // collisions from TMJ
    this.collisionData = Route333Scene.extractCollisionDataFromTiled(
      this.tmj,
      this.tmjLayer,
    );

    const bg = this.add.image(0, 0, "route333_bg").setOrigin(0);

    bg.setPosition(0, 0);

    // world bounds should match the positioned BG
    this.cameras.main.setBounds(0, 0, bg.width, bg.height);
    this.matter.world.setBounds(0, 0, bg.width, bg.height);

    this.add
      .image(0, 0, "route333_fg")
      .setOrigin(0)
      .setDepth(1000)
      .setScrollFactor(1);

    // build static bodies + sensors from TMJ
    this.collisionData.buildings.forEach((b) => {
      const xs = b.polygon.map((p) => p[0]);
      const ys = b.polygon.map((p) => p[1]);
      const minX = Math.min(...xs),
        maxX = Math.max(...xs);
      const minY = Math.min(...ys),
        maxY = Math.max(...ys);

      const x = (minX + maxX) / 2;
      const y = (minY + maxY) / 2;
      const w = maxX - minX;
      const h = maxY - minY;

      const isExit = (b.name || "").toLowerCase() === "exit";
      this.matter.add.rectangle(x, y, w, h, {
        isStatic: true,
        isSensor: isExit, // exits are sensors; walls are solid
        label: isExit ? "exit" : undefined,
      });

      if (DEBUG_COLLIDERS) {
        const color = isExit ? 0x0000ff : 0xff0000;
        this.drawCollisionDebugBox(x, y, w, h, color, 0.25);
      }
    });

    // 🚧 Build a fast obstacle index from TMJ so orbs avoid walls
    this.obstacles = buildObstacleIndex(this.collisionData.buildings);

    const playableBounds = computePlayableRect(
      this.collisionData.buildings,
      36,
    );

    // Cyan world border line for debug
    // const dbg = this.add.graphics().setDepth(9999);
    // dbg
    //   .lineStyle(2, 0x00ffff, 1)
    //   .strokeRect(
    //     playableBounds.x,
    //     playableBounds.y,
    //     playableBounds.width,
    //     playableBounds.height
    //   );

    // player
    this.player = this.matter.add
      .sprite(this.spawnPoint.x, this.spawnPoint.y, "hero", 0)
      .setBody(
        scaleAvatarBodySize(this.heroAvatarKey, { width: 45, height: 65 }),
      )
      .setScale(this.heroSpriteConfig.worldScale || 1)
      .setFixedRotation();
    this.playerOrbImage = createLocalOrbAvatarImage(
      this,
      this.player,
      this.spawnPoint,
      this.localOrbAvatarData,
      this.localOrbTextureKey,
      { depth: this.spawnPoint.y + 24 },
    );

    this.playerDebugGfx = this.add.graphics().setDepth(20000);
    const isMobile =
      this.sys.game.device.os.android || this.sys.game.device.os.iOS;
    this.cameras.main
      .startFollow(this.player, true, 0.1, 0.1)
      .setZoom(isMobile ? 1.2 : 2.0);

    // --- player freeze state ---
    this._playerFrozenUntil = 0;

    // listen for aggro from any evil orb
    this.events.on("npc-aggro", () => {
      const ms = 3000; // freeze duration
      this._playerFrozenUntil = Math.max(
        this._playerFrozenUntil,
        this.time.now + ms,
      );
    });

    // 🌐 Spawn a few NPC orbs that respect Route333’s collisions
    const DEPTH = { NPC: 400 }; // or use your scene’s depth scheme
    console.log("🧪 Has EvilOrb?", !!npcMap.EvilOrb);
    this.worldOrbs = spawnWorldOrbs(
      this,
      npcMap,
      ["RedOrb", "BlueOrb", "EvilOrb"], // pick keys that exist in your npcMap
      8, // how many to spawn
      DEPTH.NPC,
      {
        obstacles: this.obstacles,
        bounds: playableBounds,
        minClearance: 48,
        debug: DEBUG_COLLIDERS, // flip off when done
        player: this.player,
      },
    );

    // Route333Scene.jsx → inside create(), after you create this.player and spawn orbs
    this.matter.world.on("collisionstart", (ev) => {
      ev.pairs.forEach(({ bodyA, bodyB }) => {
        const goA = bodyA.gameObject;
        const goB = bodyB.gameObject;
        if (!goA || !goB) return;

        const isPlayerA = goA === this.player;
        const isPlayerB = goB === this.player;

        const isOrbA = goA.getData && goA.getData("isNPCOrb");
        const isOrbB = goB.getData && goB.getData("isNPCOrb");

        // Only care about player <-> orb (you can extend to orb <-> orb similarly)
        if ((isPlayerA && isOrbB) || (isPlayerB && isOrbA)) {
          const player = isPlayerA ? goA : goB;
          const orb = isOrbA ? goA : goB;

          //   if (player._snapLock || orb._snapLock) return; // avoid repeated snap same frame
          //   player._snapLock = orb._snapLock = true;

          // stop both
          player.setVelocity(0, 0);
          orb.setVelocity(0, 0);

          //   // snap back to last safe positions if we have them
          //   if (typeof player._lastSafeX === "number") {
          //     player.setPosition(player._lastSafeX, player._lastSafeY);
          //   }
          //   if (typeof orb._lastSafeX === "number") {
          //     orb.setPosition(orb._lastSafeX, orb._lastSafeY);
          //   }
          //   // clear the lock at end of frame
          //   this.time.delayedCall(0, () => {
          //     player._snapLock = orb._snapLock = false;
          //   });
        }
      });
    });

    // animations
    createWalkAnimations(this, "hero", this.heroAvatarKey);

    // input
    this.joystick = { x: 0, y: 0 };
    this.cursors = this.input.keyboard.createCursorKeys();
    this.keys = this.input.keyboard.addKeys({
      W: Phaser.Input.Keyboard.KeyCodes.W,
      A: Phaser.Input.Keyboard.KeyCodes.A,
      S: Phaser.Input.Keyboard.KeyCodes.S,
      D: Phaser.Input.Keyboard.KeyCodes.D,
    });

    // proximity: exit
    this.currentDoor = null;
    this.matter.world.on("collisionstart", (ev) => {
      ev.pairs.forEach(({ bodyA, bodyB }) => {
        const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
        const isPlayer =
          bodyA === this.player.body || bodyB === this.player.body;

        if (sensor && isPlayer && sensor.label === "exit") {
          this.currentDoor = "exit";
        }
      });
    });
    this.matter.world.on("collisionend", (ev) => {
      ev.pairs.forEach(({ bodyA, bodyB }) => {
        const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
        const isPlayer =
          bodyA === this.player.body || bodyB === this.player.body;
        if (sensor && isPlayer && sensor.label === "exit") {
          this.currentDoor = null;
        }
      });
    });

    // Enter to go back to city
    this._onEnter = () => {
      if (this.currentDoor !== "exit") return;

      AudioManager.playSFX("door");
      // Compute a spawn in front of PulseTowerExit (from city collisions)
      const poly = cityCollisions.buildings.find(
        (b) => b.name === "PulseTowerExit",
      )?.polygon;
      let nextSpawn = { x: 780, y: 80 };
      if (poly) {
        const xs = poly.map(([x]) => x);
        const ys = poly.map(([, y]) => y);
        nextSpawn = {
          x: (Math.min(...xs) + Math.max(...xs)) / 2,
          y: (Math.min(...ys) + Math.max(...ys)) / 2 + 10,
        };
      }

      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once("camerafadeoutcomplete", () => {
        this.scene.stop(this.scene.key);
        this.scene.start("VoidCity2Scene", {
          spawn: nextSpawn,
          returnSpawn: nextSpawn,
          heroUrl: this.heroUrl,
          heroAvatarKey: this.heroAvatarKey,
          userData: this.userData,
        });
      });
    };

    this.spaceKey = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.SPACE,
    );
    this.spaceKey.on("down", this._onEnter);
    window.addEventListener("press-enter", this._onEnter);

    this.events.once("shutdown", () => {
      this.spaceKey?.off?.("down", this._onEnter);
      window.removeEventListener("press-enter", this._onEnter);
    });

    this.cameras.main.fadeIn(300, 0, 0, 0);
  }

  update() {
    // simple WASD / arrows
    const speed = 2;
    let vx = 0,
      vy = 0;
    const { x: jx, y: jy } = this.joystick;

    const frozen = this._playerFrozenUntil > this.time.now;
    // hard stop if frozen
    if (frozen) {
      this.player.setVelocity(0, 0);
      this.player.anims.stop();
    }

    if (this.player) {
      this.player._lastSafeX = this.player.x;
      this.player._lastSafeY = this.player.y;
    }
    if (!frozen) {
      if (jx || jy) {
        vx = jx * speed;
        vy = jy * speed;
      } else {
        if (this.cursors.left.isDown || this.keys.A.isDown) vx = -speed;
        else if (this.cursors.right.isDown || this.keys.D.isDown) vx = speed;
        if (this.cursors.up.isDown || this.keys.W.isDown) vy = -speed;
        else if (this.cursors.down.isDown || this.keys.S.isDown) vy = speed;
      }

      if (vx && vy) {
        vx *= Math.SQRT1_2;
        vy *= Math.SQRT1_2;
      }

      // --- AABB block vs. NPC orbs (axis-by-axis so you can slide) ---
      if (!frozen && this.worldOrbs?.length) {
        const pRect = aabbFromBody(this.player.body);
        // test X movement alone
        if (vx !== 0) {
          const nextXRect = { ...pRect, x: pRect.x + vx };
          let blockedX = false;
          for (const orb of this.worldOrbs) {
            // each orb: use its body bounds to align with your debug boxes
            if (!orb?.body) continue;
            const oRect = aabbFromBody(orb.body);
            if (rectsOverlap(nextXRect, oRect, 0)) {
              blockedX = true;
              break;
            }
          }
          if (blockedX) vx = 0;
        }
        // test Y movement alone
        if (vy !== 0) {
          const nextYRect = { ...pRect, y: pRect.y + vy };
          let blockedY = false;
          for (const orb of this.worldOrbs) {
            if (!orb?.body) continue;
            const oRect = aabbFromBody(orb.body);
            if (rectsOverlap(nextYRect, oRect, 0)) {
              blockedY = true;
              break;
            }
          }
          if (blockedY) vy = 0;
        }
      }

      this.player.setVelocity(vx, vy);

      if (
        updateLocalOrbAvatar(
          this,
          this.player,
          this.playerOrbImage,
          this.localOrbAvatarData,
          {
            x: this.player.x,
            y: this.player.y,
            vx,
            depth: this.player.y + 24,
          },
        )
      ) {
        // Orb avatars use the image overlay instead of the walking spritesheet.
      } else if (Math.abs(vy) > Math.abs(vx)) {
        if (vy < 0) this.player.anims.play("walk-up", true);
        else if (vy > 0) this.player.anims.play("walk-down", true);
      } else if (vx < 0) this.player.anims.play("walk-left", true);
      else if (vx > 0) this.player.anims.play("walk-right", true);
      else this.player.anims.stop();
    } else {
      this.player.setVelocity(0, 0);
      this.player.anims.stop();
    }

    // ♻️ Keep each orb’s inner-glass color in sync as they move/turn
    if (this.worldOrbs) {
      for (const orb of this.worldOrbs) {
        if (orb._dbg) {
          const { boxG, circG, r } = orb._dbg;
          // clear old drawings
          boxG.clear();
          circG.clear();

          // get updated bounds
          const b = orb.getBounds();
          boxG.lineStyle(1, 0x00ff00, 1);
          boxG.strokeRect(b.x, b.y, b.width, b.height);
          boxG.fillStyle(0x00ff00, 0.25);
          boxG.fillRect(b.x, b.y, b.width, b.height);

          circG.lineStyle(2, 0xff00ff, 1);
          circG.strokeCircle(orb.body.position.x, orb.body.position.y, r);
        }

        if (orb._glass) redrawOrbGlass(this, orb, ORB_GLASS);
      }
    }

    // player debug box
    if (DEBUG_COLLIDERS) this.drawPlayerDebugBox();
  }
}
